/**
 * PortalReferralsPanel — the staff view of clinician referrals, and the triage
 * screen a collision routes to (clinician-portal P1 §6, §6.11b, D-13).
 *
 * TWO THINGS THIS SCREEN IS CAREFUL ABOUT.
 *
 * The list shows NO patient identity beyond the first name the clinician
 * submitted. Everything else — surname, phone, email, the referring note — is
 * AES-GCM ciphertext on the row, and the list endpoint does not even fetch it.
 * Opening one referral is a deliberate act that writes an audit row marked
 * `phi_accessed`, which is why "Open" is a button and not a hover card. The
 * background refresh added below reads that same projection and nothing else —
 * a poll that decrypted would make "who looked at this patient" unanswerable.
 *
 * A COLLISION IS A PHONE CALL. No date of birth is collected from a provider, so
 * the typed-name-vs-escrow comparison at signing is the only wrong-number guard,
 * and it does no fuzzy matching on purpose: a typo lands here rather than being
 * quietly accepted, because a false match attaches a stranger's consent to a real
 * patient's referral. This screen shows both names side by side so the human can
 * decide, and says plainly that confirming means they verified out of band.
 *
 * THREE THINGS THE 2026-07-26 REVIEW CHANGED.
 *
 * §C14 — the queue had no age. Collisions were listed newest-first alongside
 * everything else, so the one that had been waiting two days sat below the one
 * from ten minutes ago. Open collisions now sort to the top, oldest first, and
 * carry an SLA chip; the count is also surfaced outside this panel (the Settings
 * nav badge and Today's CollisionTriageCard), because an indicator rendered
 * inside the panel it counts is only ever seen by someone already looking.
 *
 * §C15 — the list only loaded on mount and filter change. After a resolve the
 * toast described work happening on a worker tick that the row would never
 * visibly reflect without hammering Refresh. It now polls, refetches when the
 * tab regains focus, and re-polls a few times after a resolve so the row is seen
 * to move.
 *
 * §B12 — a manual (faxed) referral had no recovery of any kind. One transient
 * Twilio timeout left it as "Couldn't text" with the consent URL already shown
 * once and gone: no staff resend existed, the clinician resend does not apply
 * (there is no clinician), and `loadForTriage` deliberately never opens the
 * escrowed token. The referral sat until the 14-day expiry shredded it. Staff
 * can now re-text it from the row and from the triage screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Eye, Loader2, Plus, RefreshCw, Send } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../../components/forms/Modal';
import { FilterBar, FilterSelect, SearchInput } from '../../components/filters';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import { relTime } from '../../lib';
import {
  COLLISION_SLA,
  canStaffResend,
  collisionSince,
  collisionSla,
  describeSend,
  describeSmsError,
  errMsg,
  listReferrals,
  readSendOutcome,
  resendReferralLink,
  summariseCollisions,
  type CollisionSla,
  type PortalReferral,
} from '../../portalReferrals';
import { Alert, Badge, type BadgeTone } from '../../components/ui';

/** The P1-D9 vocabulary, with the copy a staff member needs rather than the enum. */
const STATUS_LABEL: Record<string, string> = {
  pending_consent: 'Waiting on the patient',
  sms_failed: "Couldn't text",
  delivered: 'Text delivered',
  reminded: 'Reminded',
  viewed: 'Opened the link',
  signed_pending_artifacts: 'Signed — building records',
  consented: 'Consented',
  declined: 'Declined',
  expired: 'Expired',
  withdrawn: 'Withdrawn (STOP)',
  collision_review: 'Name mismatch — needs review',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  consented: 'green',
  signed_pending_artifacts: 'green',
  collision_review: 'red',
  sms_failed: 'amber',
  declined: 'gray',
  expired: 'gray',
  withdrawn: 'gray',
};
const toneFor = (s: string): BadgeTone => STATUS_TONE[s] || 'primary';

/** The SLA chip on a collision row. Never colour alone — each carries its words. */
const SLA_TONE: Record<CollisionSla, BadgeTone> = {
  fresh: 'gray',
  attention: 'amber',
  late: 'red',
};

const CONDITIONS = [
  { value: '', label: 'Not specified' },
  { value: 'type_2_diabetes', label: 'Type 2 diabetes' },
  { value: 'obesity_weight', label: 'Obesity / weight' },
  { value: 'cardiovascular', label: 'Cardiovascular' },
  { value: 'kidney', label: 'Kidney' },
  { value: 'liver_nash', label: 'Liver / NASH' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'other', label: 'Other' },
];

type Referral = PortalReferral;

interface TriageDetail extends Referral {
  purged: boolean;
  referred: { lastName: string | null; phone: string | null; email: string | null; note: string | null };
}

const fmt = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// ---------------------------------------------------------------------------

/** Opening this modal IS the PHI access. It is audited server-side before the
 *  response is written, and refused outright if that audit row cannot land. */
function TriageModal({ referralId, onClose, onResolved, onResent }: {
  referralId: string;
  onClose: () => void;
  onResolved: (message: string) => void;
  onResent: (message: string, consentUrl: string) => void;
}) {
  const [detail, setDetail] = useState<TriageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await api.get(`/api/crm/portal-admin/referrals/${referralId}/triage`);
        if (live) setDetail(res.data?.referral || null);
      } catch (e) {
        if (live) setError(errMsg(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [referralId]);

  const resolve = async (resolution: 'confirm' | 'reject') => {
    setBusy(resolution);
    setError(null);
    try {
      await api.post(`/api/crm/portal-admin/referrals/${referralId}/resolve-collision`, { resolution, note });
      onResolved(
        resolution === 'confirm'
          // NOT "the records will be built". A collision never keeps the
          // signature — that is the point of it — so confirming captures no
          // consent. It re-opens the link and remembers that a human checked
          // the identity, so the name that collided is accepted next time.
          ? 'Confirmed — nobody has signed yet. We will text their link again shortly, and it now accepts the name they gave. It is valid for another 7 days.'
          : 'Rejected — the referral was withdrawn and the patient details shredded.',
      );
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  /** §B12 — the staff resend, reachable from the one screen that already has
   *  the referral open. Deliberately NOT a "send the link to a new number":
   *  correcting the number is a separate, separately-audited action. */
  const resend = async () => {
    setBusy('resend');
    setError(null);
    try {
      const outcome = await resendReferralLink(referralId);
      onResent(describeSend(outcome, 'resent'), outcome.consentUrl);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const isCollision = detail?.status === 'collision_review';
  const showResend = Boolean(detail && canStaffResend(detail));
  // §B16 stores a CODE in sms_error now, because the carrier's own sentence
  // embeds the patient's phone number. A code is the right thing to store and
  // the wrong thing to read: `twilio_error_21610` (they replied STOP),
  // `twilio_error_21614` (landline) and `twilio_error_21211` (not a real number)
  // are three different phone calls. describeSmsError is where that is decided.
  const smsFailure = describeSmsError(detail?.sms_error);

  return (
    <Modal isOpen onClose={onClose} title="Referral details">
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Opening…
        </div>
      ) : error && !detail ? (
        <Alert tone="error" icon={false}>{error}</Alert>
      ) : detail ? (
        <div className="space-y-3">
          {isCollision && (
            <div className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-900 dark:bg-red-900/20 dark:text-red-200">
              <p className="flex items-start gap-2 font-medium">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                The name the patient typed does not match the name their provider referred.
              </p>
              <p className="mt-1">
                No contact was created and nothing was sent. Usually this is a mistyped digit reaching a
                stranger — call the referring practice before confirming.
              </p>
              <p className="mt-1">
                Confirming does not record consent: their signature was not kept. It re-sends their link
                and lets the name they gave through, so they still have to sign.
              </p>
              <p className="mt-1 font-medium">
                Waiting {relTime(collisionSince(detail))} · the patient was told someone would call.
              </p>
            </div>
          )}

          {detail.purged && (
            <p className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
              This referral's details were shredded when it reached <strong>{STATUS_LABEL[detail.status] || detail.status}</strong>.
              Only the record of what happened remains.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Referred by the provider as</p>
              <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                {detail.display_first_name} {detail.referred.lastName || <span className="text-gray-400">—</span>}
              </p>
              <dl className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex gap-2"><dt className="w-14 shrink-0 text-gray-400">Phone</dt><dd>{detail.referred.phone || '—'}</dd></div>
                <div className="flex gap-2"><dt className="w-14 shrink-0 text-gray-400">Email</dt><dd className="truncate">{detail.referred.email || '—'}</dd></div>
                {detail.referred.note && (
                  <div className="flex gap-2"><dt className="w-14 shrink-0 text-gray-400">Note</dt><dd>{detail.referred.note}</dd></div>
                )}
              </dl>
            </div>
            <div className={`rounded-lg border p-3 ${isCollision ? 'border-red-300 dark:border-red-800' : 'border-gray-200 dark:border-gray-700'}`}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">The patient typed</p>
              <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                {detail.typed_first_name || <span className="text-gray-400">—</span>} {detail.typed_last_name || ''}
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {detail.typed_last_name ? 'Entered on the consent page at signing.' : 'They have not signed yet.'}
              </p>
            </div>
          </div>

          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex gap-2"><dt className="w-24 shrink-0 text-gray-400">Status</dt><dd className="text-gray-900 dark:text-gray-100">{STATUS_LABEL[detail.status] || detail.status}</dd></div>
            <div className="flex gap-2"><dt className="w-24 shrink-0 text-gray-400">Source</dt><dd className="text-gray-900 dark:text-gray-100">{detail.source === 'manual' ? 'Typed in by staff' : 'Clinician portal'}</dd></div>
            <div className="flex gap-2"><dt className="w-24 shrink-0 text-gray-400">Referred</dt><dd className="text-gray-900 dark:text-gray-100">{fmt(detail.created_at)}</dd></div>
            <div className="flex gap-2"><dt className="w-24 shrink-0 text-gray-400">Link expires</dt><dd className="text-gray-900 dark:text-gray-100">{fmt(detail.consent_expires_at)}</dd></div>
          </dl>

          {smsFailure && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              <span className="font-medium">The last text did not go out.</span> {smsFailure.sentence}
              {smsFailure.attempt && (
                <span className="text-amber-800/80 dark:text-amber-200/70"> {smsFailure.attempt}</span>
              )}
            </p>
          )}

          {detail.artifact_error && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              The records build last failed with: {detail.artifact_error}. It retries on every worker tick.
            </p>
          )}

          {isCollision && (
            <>
              <div>
                <label htmlFor="triage-note" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  What did you find out?
                </label>
                <textarea
                  id="triage-note"
                  rows={2}
                  className={fieldCls}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. called the practice — patient recently married, same person"
                />
              </div>
              {error && (
                <Alert tone="error" icon={false}>{error}</Alert>
              )}
              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={Boolean(busy)}>Close</button>
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 sm:min-h-0"
                  onClick={() => resolve('reject')}
                  disabled={Boolean(busy)}
                >
                  {busy === 'reject' && <Loader2 className="h-4 w-4 animate-spin" />}
                  Wrong person — withdraw
                </button>
                <button type="button" className={primaryBtnCls} onClick={() => resolve('confirm')} disabled={Boolean(busy)}>
                  {busy === 'confirm' && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  I verified it — confirm
                </button>
              </div>
            </>
          )}
          {!isCollision && (
            <>
              {error && (
                <Alert tone="error" icon={false}>{error}</Alert>
              )}
              <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                {showResend && (
                  <p className="mr-auto text-xs text-gray-500 dark:text-gray-400">
                    Sends the same link again — nothing about the referral changes.
                  </p>
                )}
                <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={Boolean(busy)}>Close</button>
                {showResend && (
                  <button type="button" className={primaryBtnCls} onClick={resend} disabled={Boolean(busy)}>
                    {busy === 'resend' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                    Text the link again
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/** §6.11b — a referral that arrived by fax or phone. The patient gets exactly
 *  what a portal referral's patient gets; they cannot tell the difference and
 *  their consent must not depend on it. */
function ManualReferralModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (message: string, consentUrl: string) => void;
}) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', note: '',
    condition_hint: '', provider_name: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: string) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSend = form.first_name.trim() && form.last_name.trim() && form.phone.trim() && !busy;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/portal-admin/referrals', form);
      // §C16: the API has always answered with `sms.sent` and this screen has
      // always claimed the link "has been texted" regardless. A staff member who
      // believes a text went out does not hand over the QR and does not call —
      // and finds out fourteen days later, when the escrow expires. One helper
      // writes all four outcomes so the two send paths cannot drift apart.
      const outcome = readSendOutcome(res.data);
      onCreated(describeSend(outcome, 'created'), outcome.consentUrl);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Enter a referral">
      <div className="space-y-3">
        <p className="rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-900 dark:bg-primary-900/20 dark:text-primary-200">
          For a referral that came in by fax or phone. The patient is texted the same authorization link a
          portal referral sends, and nothing about them is stored in the clear until they sign.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="mr-first" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">First name</label>
            <input id="mr-first" className={fieldCls} value={form.first_name} onChange={set('first_name')} />
          </div>
          <div>
            <label htmlFor="mr-last" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Last name</label>
            <input id="mr-last" className={fieldCls} value={form.last_name} onChange={set('last_name')} aria-describedby="mr-last-help" />
            <p id="mr-last-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The patient types this again at signing; a mismatch routes to review rather than creating a record.
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="mr-phone" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Mobile</label>
            <input id="mr-phone" className={fieldCls} value={form.phone} onChange={set('phone')} />
          </div>
          <div>
            <label htmlFor="mr-email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input id="mr-email" type="email" className={fieldCls} value={form.email} onChange={set('email')} />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="mr-cond" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Reason</label>
            <select id="mr-cond" className={fieldCls} value={form.condition_hint} onChange={set('condition_hint')}>
              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="mr-provider" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Referring provider <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input id="mr-provider" className={fieldCls} value={form.provider_name} onChange={set('provider_name')} aria-describedby="mr-provider-help" />
            {/* Honest about its reach: the name is used to compose the FIRST
                text and is not kept on the referral, so it is absent from this
                list, from the triage screen, from any later reminder, and from
                the signed authorization. Storing it needs a column — see the
                review notes; until then, saying so beats implying otherwise. */}
            <p id="mr-provider-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Named in the first text so the patient recognises who sent them. It is not stored on the
              referral, so later reminders say "your provider".
            </p>
          </div>
        </div>
        <div>
          <label htmlFor="mr-note" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Note <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <textarea id="mr-note" rows={2} className={fieldCls} value={form.note} onChange={set('note')} />
        </div>
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={primaryBtnCls} onClick={create} disabled={!canSend}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Create and text the link
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/** §C15 — how often the list re-reads itself while the tab is in front. 45s
 *  sits between "a resolve feels acknowledged" and "a projection endpoint is
 *  being asked a question nobody is reading"; the poll is suspended entirely
 *  while the tab is hidden. */
const POLL_MS = 45_000;

/** Silent re-reads after a resolve, in ms from the action. The confirm path
 *  hands the row to a worker (it re-arms the send and the status leaves
 *  `collision_review`), so the first refetch usually shows nothing new — these
 *  catch the move without the staff member pressing Refresh to find out. */
const RESOLVE_REPOLL_MS = [3_000, 8_000, 20_000];

interface PortalReferralsPanelProps {
  /** Status filter to open on — set by the deep link from the Today card and
   *  the Settings nav badge, both of which mean "show me the mismatches". */
  initialStatus?: string;
  /** Reports the open-collision count on every load, so the tab strip above can
   *  badge it without running a second poll of its own. */
  onCollisionCount?: (n: number) => void;
}

export default function PortalReferralsPanel({ initialStatus = '', onCollisionCount }: PortalReferralsPanelProps) {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; url?: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [search, setSearch] = useState('');
  const [triageId, setTriageId] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Timers for the post-resolve re-polls, cleared on unmount so a resolve that
  // is immediately followed by navigating away does not keep firing requests.
  const repollTimers = useRef<number[]>([]);
  const clearRepolls = useCallback(() => {
    repollTimers.current.forEach((t) => window.clearTimeout(t));
    repollTimers.current = [];
  }, []);

  /**
   * `silent` keeps the table on screen while it refreshes. A poll that swapped
   * the rows for a spinner every 45 seconds would be worse than not polling —
   * it would move the button out from under a cursor mid-click.
   */
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const rows = await listReferrals(statusFilter || undefined);
      setReferrals(rows);
      setError(null);
    } catch (e) {
      // A failed BACKGROUND refresh keeps the rows it already has and stays
      // quiet; only a refresh the staff member asked for reports itself.
      if (!silent) setError(errMsg(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // §C15: poll while visible, and refetch the moment the tab comes back. Someone
  // who calls the practice, resolves it in another window, or simply leaves this
  // open on a second monitor should not be reading minutes-old state.
  useEffect(() => {
    const tick = () => { if (!document.hidden) load(true); };
    const timer = window.setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(true); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => clearRepolls, [clearRepolls]);

  /** Refetch now, then a few more times as the worker gets to it. */
  const loadAfterAction = useCallback(() => {
    load(true);
    clearRepolls();
    repollTimers.current = RESOLVE_REPOLL_MS.map((ms) => window.setTimeout(() => load(true), ms));
  }, [load, clearRepolls]);

  /** §B12 — re-text a manual referral's link straight from its row. */
  const resendFromRow = async (r: Referral) => {
    setResendingId(r.id);
    setError(null);
    try {
      const outcome = await resendReferralLink(r.id);
      setNotice({ text: describeSend(outcome, 'resent'), url: outcome.consentUrl || undefined });
      loadAfterAction();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setResendingId(null);
    }
  };

  const collisions = useMemo(() => summariseCollisions(referrals), [referrals]);

  // Reported OUT of the fetch closure on purpose (the FollowUpsDuePanel idiom):
  // a caller that passes an inline lambda would otherwise change `load` on every
  // render, and `useEffect(…, [load])` would turn that into a request loop.
  //
  // ONLY from a view that could see a collision. When the status filter excludes
  // `collision_review` this list is silent about them, not authoritative that
  // there are none — reporting 0 from here would clear the tab badge the moment
  // someone filtered to "Consented", which is exactly the kind of quietly-wrong
  // indicator §C14 is about.
  const reportsCollisions = statusFilter === '' || statusFilter.includes('collision_review');
  useEffect(() => {
    if (reportsCollisions) onCollisionCount?.(collisions.total);
  }, [reportsCollisions, collisions.total, onCollisionCount]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Only the first name and what the patient typed are searchable — the rest
    // is ciphertext the list never fetched.
    const matched = q
      ? referrals.filter((r) =>
        [r.display_first_name, r.typed_first_name, r.typed_last_name]
          .filter(Boolean).join(' ').toLowerCase().includes(q))
      : referrals;
    // §C14: open collisions to the top, OLDEST first. The server orders by
    // created_at desc, which buries the mismatch that has been waiting two days
    // under the one from ten minutes ago — the exact inversion of the order a
    // queue with an SLA needs. Everything else keeps the server's order.
    const now = Date.now();
    return [...matched].sort((a, b) => {
      const ac = a.status === 'collision_review';
      const bc = b.status === 'collision_review';
      if (ac !== bc) return ac ? -1 : 1;
      if (!ac) return 0;
      return new Date(collisionSince(a, now)).getTime() - new Date(collisionSince(b, now)).getTime();
    });
  }, [referrals, search]);

  return (
    <div className="space-y-3">
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by first name" />
        <FilterSelect
          label="Status"
          auto
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'collision_review', label: 'Needs review' },
            { value: 'pending_consent,delivered,reminded,viewed', label: 'Waiting on the patient' },
            { value: 'sms_failed', label: "Couldn't text" },
            { value: 'signed_pending_artifacts,consented', label: 'Consented' },
            { value: 'declined,expired,withdrawn', label: 'Closed' },
          ]}
        />
        <button type="button" className={secondaryBtnCls} onClick={() => load()} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button type="button" className={primaryBtnCls} onClick={() => setShowManual(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Enter a referral
        </button>
      </FilterBar>

      {collisions.total > 0 && (
        <p className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            {collisions.total} referral{collisions.total === 1 ? '' : 's'} where the patient's name did not match. Nothing was
            created for {collisions.total === 1 ? 'it' : 'them'} — someone needs to call the practice.
            {collisions.oldestSince && ` Oldest waiting ${relTime(collisions.oldestSince)}.`}
          </span>
          {collisions.late > 0 && (
            <Badge tone={SLA_TONE.late}>
              {collisions.late} over {COLLISION_SLA.lateHours}h
            </Badge>
          )}
        </p>
      )}
      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}
      {notice && (
        <Alert tone="success" icon={false}>
          <p>{notice.text}</p>
          {notice.url && (
            <p className="mt-1 break-all font-mono text-xs">
              {notice.url}
              <span className="ml-2 font-sans not-italic text-green-700 dark:text-green-400">
                — shown once; only its hash is stored.
              </span>
            </p>
          )}
        </Alert>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Referred</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
                </td></tr>
              )}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  No referrals yet.
                </td></tr>
              )}
              {!loading && shown.map((r) => {
                const isCollision = r.status === 'collision_review';
                const sla = isCollision ? collisionSla(r) : null;
                return (
                  <tr key={r.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      <span className="font-medium">{r.display_first_name}</span>
                      {r.typed_last_name && (
                        <span className="ml-1 text-gray-500 dark:text-gray-400">{r.typed_last_name}</span>
                      )}
                      {r.sms_blocked && (
                        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          opted out
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={toneFor(r.status)}>{STATUS_LABEL[r.status] || r.status}</Badge>
                        {/* The SLA signal §C14 asked for. Words, not just colour:
                            "waiting 2d" is the number someone acts on. */}
                        {sla && (
                          <Badge tone={SLA_TONE[sla]}>
                            waiting {relTime(collisionSince(r))}
                            {sla === 'late' ? ' · overdue' : ''}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {r.source === 'manual' ? 'Staff entry' : 'Portal'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canStaffResend(r) && (
                          <button
                            type="button"
                            className={secondaryBtnCls}
                            onClick={() => resendFromRow(r)}
                            disabled={resendingId === r.id}
                            title="Text the same authorization link again"
                            aria-label={`Text the authorization link to ${r.display_first_name} again`}
                          >
                            {resendingId === r.id
                              ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                              : <Send className="mr-1.5 h-4 w-4" />}
                            Re-text
                          </button>
                        )}
                        <button
                          type="button"
                          className={secondaryBtnCls}
                          onClick={() => setTriageId(r.id)}
                          title="Opening this records a PHI access in the audit log"
                        >
                          <Eye className="mr-1.5 h-4 w-4" /> {isCollision ? 'Review' : 'Open'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {triageId && (
        <TriageModal
          referralId={triageId}
          onClose={() => setTriageId(null)}
          onResolved={(message) => { setNotice({ text: message }); loadAfterAction(); }}
          onResent={(message, url) => { setNotice({ text: message, url: url || undefined }); loadAfterAction(); }}
        />
      )}
      {showManual && (
        <ManualReferralModal
          onClose={() => setShowManual(false)}
          onCreated={(text, url) => { setNotice({ text, url: url || undefined }); loadAfterAction(); }}
        />
      )}
    </div>
  );
}
